/**
 * OBJ Asset Service
 * Provides automatic OBJ model compression and edge caching for 3D assets stored in Supabase.
 * Enables fast global delivery and reduces load times across all property pages.
 */

import { supabase } from '../lib/supabase';

// ─── Constants ─────────────────────────────────────────────────────────────────
const CACHE_PREFIX = 'kapital_obj_cache_';
const CACHE_META_KEY = 'kapital_obj_cache_meta';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MAX_CACHE_ENTRIES = 100;
const BUCKET = 'property-assets';

// In-memory LRU cache for ultra-fast repeated access within a session
const memoryCache = new Map();
const MAX_MEMORY_ENTRIES = 50;

// ─── Compression ───────────────────────────────────────────────────────────────

/**
 * Compresses an OBJ file text by:
 * - Removing comment lines (# ...)
 * - Stripping blank lines
 * - Reducing floating-point precision from 6+ decimals to 4
 * - Removing redundant whitespace
 *
 * @param {string} text - Raw OBJ file content
 * @returns {{ compressed: string, originalSize: number, compressedSize: number, ratio: number }}
 */
export const compressObjText = (text) => {
  const originalSize = new Blob([text]).size;

  const lines = text.split('\n');
  const compressed = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip comment lines and blank lines
    if (!trimmed || trimmed.startsWith('#')) continue;

    // Reduce float precision on vertex/normal/texcoord lines
    if (
      trimmed.startsWith('v ') ||
      trimmed.startsWith('vn ') ||
      trimmed.startsWith('vt ')
    ) {
      // Replace floats with up to 4 decimal places
      const reduced = trimmed.replace(/-?\d+\.\d{5,}/g, (match) =>
        parseFloat(match).toFixed(4)
      );
      compressed.push(reduced);
    } else {
      // Collapse multiple spaces to single space
      compressed.push(trimmed.replace(/\s+/g, ' '));
    }
  }

  const compressedText = compressed.join('\n');
  const compressedSize = new Blob([compressedText]).size;
  const ratio = originalSize > 0 ? ((originalSize - compressedSize) / originalSize) * 100 : 0;

  return {
    compressed: compressedText,
    originalSize,
    compressedSize,
    ratio: Math.round(ratio * 10) / 10,
  };
};

/**
 * Compresses a File object containing OBJ data.
 * Returns a new File with the compressed content.
 *
 * @param {File} file - Original .obj File
 * @returns {Promise<{ file: File, stats: object }>}
 */
export const compressObjFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result || '';
        const result = compressObjText(text);
        const compressedBlob = new Blob([result.compressed], { type: 'model/obj' });
        const compressedFile = new File([compressedBlob], file.name, {
          type: 'model/obj',
          lastModified: Date.now(),
        });
        resolve({
          file: compressedFile,
          stats: {
            originalSize: result.originalSize,
            compressedSize: result.compressedSize,
            ratio: result.ratio,
          },
        });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read OBJ file for compression'));
    reader.readAsText(file);
  });

// ─── Cache Helpers ─────────────────────────────────────────────────────────────

const getCacheMeta = () => {
  try {
    const raw = localStorage.getItem(CACHE_META_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveCacheMeta = (meta) => {
  try {
    localStorage.setItem(CACHE_META_KEY, JSON.stringify(meta));
  } catch {
    // Storage quota exceeded — evict oldest entries
    evictOldestCacheEntries(10);
  }
};

const evictOldestCacheEntries = (count = 5) => {
  try {
    const meta = getCacheMeta();
    const entries = Object.entries(meta).sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
    const toEvict = entries.slice(0, count);
    for (const [key] of toEvict) {
      localStorage.removeItem(CACHE_PREFIX + key);
      delete meta[key];
    }
    localStorage.setItem(CACHE_META_KEY, JSON.stringify(meta));
  } catch {
    // Ignore
  }
};

// ─── Edge Caching ──────────────────────────────────────────────────────────────

/**
 * Generates a stable cache key from a Supabase storage path or public URL.
 * @param {string} pathOrUrl
 * @returns {string}
 */
export const getCacheKey = (pathOrUrl) => {
  // Normalize: strip query params and use last path segments
  try {
    const url = new URL(pathOrUrl);
    return url.pathname.replace(/^\/+/, '').replace(/\//g, '_');
  } catch {
    return pathOrUrl.replace(/[^a-zA-Z0-9._-]/g, '_');
  }
};

/**
 * Stores a resolved public URL in both memory and localStorage cache.
 * @param {string} key - Cache key
 * @param {string} publicUrl - Resolved CDN/public URL
 * @param {object} [metadata] - Optional metadata (size, stats, etc.)
 */
export const cacheAssetUrl = (key, publicUrl, metadata = {}) => {
  const now = Date.now();

  // Memory cache (LRU eviction)
  if (memoryCache.size >= MAX_MEMORY_ENTRIES) {
    const firstKey = memoryCache.keys().next().value;
    memoryCache.delete(firstKey);
  }
  memoryCache.set(key, { url: publicUrl, cachedAt: now, ...metadata });

  // Persistent localStorage cache
  try {
    const meta = getCacheMeta();

    // Evict if over limit
    const keys = Object.keys(meta);
    if (keys.length >= MAX_CACHE_ENTRIES) {
      evictOldestCacheEntries(10);
    }

    meta[key] = { cachedAt: now, lastAccessed: now, expiresAt: now + CACHE_TTL_MS, ...metadata };
    saveCacheMeta(meta);

    localStorage.setItem(
      CACHE_PREFIX + key,
      JSON.stringify({ url: publicUrl, cachedAt: now })
    );
  } catch {
    // Ignore storage errors
  }
};

/**
 * Retrieves a cached public URL for an asset.
 * Returns null if not cached or expired.
 * @param {string} key - Cache key
 * @returns {string|null}
 */
export const getCachedAssetUrl = (key) => {
  // Check memory cache first (fastest)
  if (memoryCache.has(key)) {
    const entry = memoryCache.get(key);
    if (entry?.url) return entry.url;
  }

  // Check localStorage
  try {
    const meta = getCacheMeta();
    const entryMeta = meta[key];
    if (!entryMeta) return null;

    // Check expiry
    if (Date.now() > entryMeta.expiresAt) {
      // Expired — remove
      localStorage.removeItem(CACHE_PREFIX + key);
      delete meta[key];
      saveCacheMeta(meta);
      return null;
    }

    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;

    const entry = JSON.parse(raw);

    // Update last accessed
    entryMeta.lastAccessed = Date.now();
    saveCacheMeta(meta);

    // Warm memory cache
    memoryCache.set(key, { url: entry.url, cachedAt: entry.cachedAt });

    return entry.url || null;
  } catch {
    return null;
  }
};

/**
 * Invalidates (removes) a cached asset URL.
 * @param {string} key - Cache key
 */
export const invalidateCachedAsset = (key) => {
  memoryCache.delete(key);
  try {
    const meta = getCacheMeta();
    delete meta[key];
    saveCacheMeta(meta);
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch {
    // Ignore
  }
};

/**
 * Clears all cached OBJ asset URLs.
 */
export const clearAllAssetCache = () => {
  memoryCache.clear();
  try {
    const meta = getCacheMeta();
    for (const key of Object.keys(meta)) {
      localStorage.removeItem(CACHE_PREFIX + key);
    }
    localStorage.removeItem(CACHE_META_KEY);
  } catch {
    // Ignore
  }
};

/**
 * Returns cache statistics for display in the asset management UI.
 * @returns {{ totalEntries: number, memoryEntries: number, oldestEntry: string|null }}
 */
export const getCacheStats = () => {
  const meta = getCacheMeta();
  const entries = Object.values(meta);
  const oldest = entries.length
    ? new Date(Math.min(...entries.map((e) => e.cachedAt))).toLocaleDateString('fr-FR')
    : null;
  return {
    totalEntries: entries.length,
    memoryEntries: memoryCache.size,
    oldestEntry: oldest,
  };
};

// ─── Upload with Compression + Caching ────────────────────────────────────────

/**
 * Compresses an OBJ file, uploads it to Supabase Storage, and caches the resulting URL.
 *
 * @param {File} file - Original .obj File
 * @param {string} storagePath - Destination path in the bucket (e.g. "obj-models/prop-id/model.obj")
 * @param {{ onProgress?: (pct: number) => void }} [options]
 * @returns {Promise<{ publicUrl: string, compressionStats: object, cacheKey: string }>}
 */
export const uploadCompressedObjAsset = async (file, storagePath, options = {}) => {
  const { onProgress } = options;

  // Step 1: Compress
  onProgress?.(10);
  const { file: compressedFile, stats: compressionStats } = await compressObjFile(file);
  onProgress?.(30);

  // Step 2: Upload compressed file
  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, compressedFile, {
      contentType: 'model/obj',
      upsert: false,
    });

  if (uploadError) throw uploadError;
  onProgress?.(80);

  // Step 3: Get public URL
  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  const publicUrl = urlData?.publicUrl;
  onProgress?.(90);

  // Step 4: Cache the URL
  const cacheKey = getCacheKey(storagePath);
  cacheAssetUrl(cacheKey, publicUrl, {
    size: compressionStats.compressedSize,
    originalSize: compressionStats.originalSize,
    compressionRatio: compressionStats.ratio,
  });
  onProgress?.(100);

  return { publicUrl, compressionStats, cacheKey };
};

/**
 * Resolves the public URL for an OBJ asset, using cache when available.
 * Falls back to generating a fresh Supabase public URL.
 *
 * @param {string} storagePath - Path in the bucket or full public URL
 * @returns {string} Resolved public URL
 */
export const resolveObjAssetUrl = (storagePath) => {
  if (!storagePath) return null;

  // If it's already a full URL, use it directly but still cache it
  if (storagePath.startsWith('http')) {
    const key = getCacheKey(storagePath);
    const cached = getCachedAssetUrl(key);
    if (cached) return cached;
    // Cache the URL as-is for future lookups
    cacheAssetUrl(key, storagePath);
    return storagePath;
  }

  const key = getCacheKey(storagePath);
  const cached = getCachedAssetUrl(key);
  if (cached) return cached;

  // Generate fresh public URL and cache it
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  const url = data?.publicUrl;
  if (url) cacheAssetUrl(key, url);
  return url || null;
};

/**
 * Prefetches and caches OBJ asset URLs for a list of storage paths.
 * Call this on property page load to warm the cache for all 3D assets.
 *
 * @param {string[]} pathsOrUrls - Array of storage paths or public URLs
 */
export const prefetchObjAssets = (pathsOrUrls = []) => {
  for (const path of pathsOrUrls) {
    if (path) resolveObjAssetUrl(path);
  }
};

export default {
  compressObjText,
  compressObjFile,
  uploadCompressedObjAsset,
  resolveObjAssetUrl,
  prefetchObjAssets,
  getCacheKey,
  cacheAssetUrl,
  getCachedAssetUrl,
  invalidateCachedAsset,
  clearAllAssetCache,
  getCacheStats,
};
